
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Database, AlertTriangle } from "lucide-react";
import { InventoryMatch } from "@/hooks/color-check/types";
import { sortColorAnalysisMatches } from "@/utils/inventory/sortUtils";

interface InventoryMatchesListProps {
  inventoryMatches: InventoryMatch[];
  hasInventoryMatches: boolean;
  hasExactColorMatches: boolean;
}

const InventoryMatchesList = ({ 
  inventoryMatches, 
  hasInventoryMatches, 
  hasExactColorMatches 
}: InventoryMatchesListProps) => {
  // Sort matches using enhanced logic
  const sortedMatches = sortColorAnalysisMatches(inventoryMatches);
  
  const getMatchClasses = (match: InventoryMatch) => {
    const isExactColor = match.colorMatch;
    const isExactSize = match.sizeMatch;
    const isPerfect = isExactColor && isExactSize;
    const isProblematicLocation = match.locationQuality === 'problematic';
    
    let bgColor, textColor;
    if (isProblematicLocation) {
      bgColor = 'bg-red-50 border-red-200';
      textColor = 'text-red-800';
    } else if (isPerfect) {
      bgColor = 'bg-green-50 border-green-200';
      textColor = 'text-green-800';
    } else if (isExactColor) {
      bgColor = 'bg-blue-50 border-blue-200';
      textColor = 'text-blue-800';
    } else if (isExactSize) {
      bgColor = 'bg-orange-50 border-orange-200';
      textColor = 'text-orange-800';
    } else {
      bgColor = 'bg-yellow-50 border-yellow-200';
      textColor = 'text-yellow-800';
    }
    
    return { bgColor, textColor };
  };

  const getMatchBadge = (match: InventoryMatch) => {
    const isExactColor = match.colorMatch;
    const isExactSize = match.sizeMatch;
    const isPerfect = isExactColor && isExactSize;
    const isProblematicLocation = match.locationQuality === 'problematic';
    
    if (isProblematicLocation) {
      return (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          LOCATIE
        </Badge>
      );
    }
    
    return (
      <Badge className={isPerfect ? 'bg-green-100 text-green-800' :
                      isExactColor ? 'bg-blue-100 text-blue-800' :
                      isExactSize ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'}>
        {isPerfect ? 'PERFECT' :
         isExactColor ? 'KLEUR' :
         isExactSize ? 'MAAT' :
         'ALT'}
      </Badge>
    );
  };

  const getLocationWarning = (match: InventoryMatch) => {
    if (match.locationQuality === 'problematic') {
      return (
        <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
          <AlertTriangle className="h-3 w-3" />
          <span>Let op: locatie "{match.locatie}" - mogelijk niet beschikbaar</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <h4 className="font-medium text-sm mb-2 flex items-center">
        <Database className="mr-1 h-4 w-4 text-green-500" />
        Alle Matches ({sortedMatches.length})
        {hasExactColorMatches && (
          <Badge variant="outline" className="ml-2 text-xs bg-blue-50">
            {sortedMatches.filter(m => m.colorMatch).length} kleur matches
          </Badge>
        )}
      </h4>
      
      {hasInventoryMatches ? (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {sortedMatches.slice(0, 5).map((match, index) => {
            const { bgColor, textColor } = getMatchClasses(match);
            const isExactColor = match.colorMatch;
            const isExactSize = match.sizeMatch;
            const isProblematicLocation = match.locationQuality === 'problematic';
            
            return (
              <div key={index} className={`p-3 rounded-lg border ${bgColor}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium text-sm ${textColor}`}>
                    {match.product_naam}
                  </span>
                  <div className="flex gap-1">
                    {getMatchBadge(match)}
                    <Badge variant="outline" className="text-xs">
                      {Math.round(match.matchScore)}%
                    </Badge>
                  </div>
                </div>
                
                {/* Location warning for problematic locations */}
                {getLocationWarning(match)}
                
                {/* Color and size comparison */}
                <div className="grid grid-cols-2 gap-2 mb-1 text-xs">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600">Kleur:</span>
                    <span className={isExactColor ? 'text-green-700 font-medium' : 'text-red-700'}>
                      {isExactColor ? 'Juist' : 'Verkeerd'}
                    </span>
                  </div>
                  {match.requestedSize && (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-600">Maat:</span>
                      <span className={isExactSize ? 'text-green-700 font-medium' : 'text-orange-700'}>
                        {match.eenheid || 'Onbekend'}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>ID: {match.product_id}</div>
                  <div>Voorraad: {match.voorraad}</div>
                  <div>Locatie: {match.locatie || 'Onbekend'}</div>
                  <div>Magazijn: {match.magazijn} {match.magazijn === 2 ? '(Goes)' : ''}</div>
                </div>
                <p className={`text-xs mt-1 font-medium ${textColor.replace('800', '600')}`}>
                  {match.matchReason}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-700">
            Geen directe matches gevonden. Probeer een externe aanvraag.
          </p>
        </div>
      )}
    </div>
  );
};

export default InventoryMatchesList;
